// @systeme: interface
// Dans Moi : où en est l'abonnement, et quoi faire. S'abonner (avec la renonciation
// expresse à la rétractation, obligatoire), gérer chez Stripe, arrêter en trois gestes
// (l'app reste entière jusqu'à la fin de la période payée), reprendre. Jamais de
// culpabilisation : un essai fini, c'est « l'assistant se repose », pas une alerte.
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { EtatAbonnement, EtatQuota } from '@foyer/shared';
import { Link as LienRouteur, useSearchParams } from 'react-router';
import { api } from '../lib/api';
import { formaterDateComplete, jourDe } from '../lib/dates';
import { textes } from '../lib/textes';
import { useSessionConnectee } from '../session/SessionContext';
import { useMessages } from './Messages';

const NOMS_QUOTAS = { vocaux_mois: 'vocaux', photos_mois: 'photos' } as const;

function phraseQuota(q: EtatQuota): string {
  if (q.limite === 0 || q.raison === 'abonnement') return '';
  if (q.decision === 'DEPASSE') return textes.quotaEpuise(NOMS_QUOTAS[q.type]);
  return textes.quotaRestant(q.restant, NOMS_QUOTAS[q.type]);
}

function prix(euros: number): string {
  return `${euros.toFixed(2).replace('.', ',')} € par mois`;
}

/** La phrase qui dit où on en est. Le silence est une réponse : rien d'alarmant. */
export function phraseEtat(e: EtatAbonnement): string {
  const plan = e.plans.find((p) => p.cle === e.planCle);
  switch (e.statut) {
    case 'essai':
      if (e.modeAllege) return 'Ton essai est terminé. Tout est là, et tout marche à la main.';
      return e.essaiFinLe
        ? `Essai gratuit, jusqu’au ${formaterDateComplete(e.essaiFinLe)}.`
        : 'Essai gratuit.';
    case 'actif':
      return `${plan?.nom ?? 'Abonnée'}${e.prixTtc !== null ? `, ${prix(e.prixTtc)}` : ''}.`;
    case 'resilie':
      return e.periodeFinLe && !e.modeAllege
        ? `Ton abonnement s’arrête le ${formaterDateComplete(jourDe(e.periodeFinLe))}. Jusque-là, rien ne change.`
        : 'Ton abonnement est arrêté. Tout est là, et tout marche à la main.';
    case 'impaye':
      return 'Le dernier paiement n’est pas passé. L’assistant se repose en attendant ; rien n’est perdu.';
    case 'expire':
      return 'Ton essai est terminé. Tout est là, et tout marche à la main.';
  }
}

export function SectionAbonnement({ quotas }: { quotas: EtatQuota[] }) {
  const session = useSessionConnectee();
  const { dire, direErreur } = useMessages();
  const [parametres, setParametres] = useSearchParams();
  const [etat, setEtat] = useState<EtatAbonnement | undefined>();
  const [dialogue, setDialogue] = useState(false);
  const [planChoisi, setPlanChoisi] = useState<'essentiel' | 'complet'>('complet');
  const [renonce, setRenonce] = useState(false);
  const [confirmerArret, setConfirmerArret] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const titulaire = session.espace.role === 'titulaire';

  useEffect(() => {
    api.abonnement.etat().then(setEtat).catch(direErreur);
  }, [direErreur]);

  // Retour de la page de paiement : un mot, puis on nettoie l'adresse.
  useEffect(() => {
    const retour = parametres.get('paiement');
    if (!retour) return;
    dire(retour === 'ok' ? textes.paiementOk : textes.paiementAnnule);
    const suivants = new URLSearchParams(parametres);
    suivants.delete('paiement');
    setParametres(suivants, { replace: true });
  }, [parametres, setParametres, dire]);

  async function payer() {
    if (!renonce) return;
    setEnCours(true);
    try {
      const { url } = await api.abonnement.payer({
        planCle: planChoisi,
        renonciationRetractation: true,
      });
      window.location.assign(url);
    } catch (e) {
      setEnCours(false);
      direErreur(e);
    }
  }

  async function ouvrirPortail() {
    setEnCours(true);
    try {
      const { url } = await api.abonnement.portail();
      window.location.assign(url);
    } catch (e) {
      setEnCours(false);
      direErreur(e);
    }
  }

  async function arreter() {
    try {
      setEtat(await api.abonnement.resilier());
      setConfirmerArret(false);
      dire('C’est noté. Ton abonnement s’arrêtera à la fin de la période déjà payée.');
    } catch (e) {
      direErreur(e);
    }
  }

  async function reprendre() {
    try {
      setEtat(await api.abonnement.reprendre());
      dire('Ton abonnement continue. Merci d’être là.');
    } catch (e) {
      direErreur(e);
    }
  }

  if (!etat) return null;

  const plansPayants = etat.plans.filter((p) => p.prixTtc > 0);
  const peutPayer = etat.paiementConfigure && plansPayants.length > 0;
  const abonnee = etat.statut === 'actif';
  const arretDemande = etat.resiliationDemandeeLe !== null;

  return (
    <Box>
      <Typography variant="h2" component="h2" gutterBottom>
        Mon abonnement
      </Typography>
      <Typography>{phraseEtat(etat)}</Typography>
      {abonnee && arretDemande && etat.periodeFinLe && (
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          Arrêt prévu le {formaterDateComplete(jourDe(etat.periodeFinLe))}. Jusque-là, rien ne
          change.
        </Typography>
      )}
      {abonnee && !arretDemande && etat.periodeFinLe && (
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          Prochaine échéance le {formaterDateComplete(jourDe(etat.periodeFinLe))}.
        </Typography>
      )}
      {etat.prixAVenir && (
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          Nouveau tarif à partir du {formaterDateComplete(jourDe(etat.prixAVenir.le))} :{' '}
          {prix(etat.prixAVenir.prixTtc)}. D’ici là, rien ne change.
        </Typography>
      )}
      {quotas.map((q) => {
        const phrase = phraseQuota(q);
        return phrase ? (
          <Typography key={q.type} color="text.secondary" sx={{ mt: 0.5 }}>
            {phrase}
          </Typography>
        ) : null;
      })}

      {titulaire && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          {!abonnee && etat.statut !== 'impaye' && (
            <>
              {peutPayer ? (
                <Button variant="contained" onClick={() => setDialogue(true)}>
                  S’abonner
                </Button>
              ) : (
                <Typography color="text.secondary">
                  Les formules ouvrent bientôt. En attendant, tout continue de marcher.
                </Typography>
              )}
            </>
          )}
          {(abonnee || etat.statut === 'impaye') && (
            <Button variant="outlined" disabled={enCours} onClick={() => void ouvrirPortail()}>
              Gérer mon abonnement
            </Button>
          )}
          {abonnee && !arretDemande && !confirmerArret && (
            <Button color="inherit" onClick={() => setConfirmerArret(true)}>
              Arrêter mon abonnement
            </Button>
          )}
          {abonnee && arretDemande && (
            <Button variant="outlined" onClick={() => void reprendre()}>
              Reprendre mon abonnement
            </Button>
          )}
        </Stack>
      )}
      {confirmerArret && (
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          <Typography>
            Ton abonnement s’arrêtera à la fin de la période déjà payée
            {etat.periodeFinLe ? `, le ${formaterDateComplete(jourDe(etat.periodeFinLe))}` : ''}.
            Jusque-là, rien ne change, et tout ce que tu as noté reste à toi.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button color="error" variant="contained" onClick={() => void arreter()}>
              Oui, arrêter
            </Button>
            <Button color="inherit" onClick={() => setConfirmerArret(false)}>
              Finalement non
            </Button>
          </Stack>
        </Stack>
      )}

      <Dialog open={dialogue} onClose={() => !enCours && setDialogue(false)} fullWidth>
        <DialogTitle>S’abonner</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={planChoisi}
            onChange={(e) =>
              setPlanChoisi(e.target.value === 'essentiel' ? 'essentiel' : 'complet')
            }
          >
            {plansPayants.map((p) => (
              <FormControlLabel
                key={p.cle}
                value={p.cle}
                control={<Radio />}
                sx={{ alignItems: 'flex-start', mb: 1 }}
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      {p.nom} — {prix(p.prixTtc)}
                    </Typography>
                    {p.description && (
                      <Typography variant="body2" color="text.secondary">
                        {p.description}
                      </Typography>
                    )}
                  </Box>
                }
              />
            ))}
          </RadioGroup>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Sans engagement : tu arrêtes quand tu veux, l’abonnement court jusqu’à la fin du mois
            payé. TVA non applicable (art. 293 B du CGI). En payant, tu acceptes les{' '}
            <LienRouteur to="/juridique/cgv" target="_blank" style={{ color: 'inherit' }}>
              conditions de vente
            </LienRouteur>
            .
          </Typography>
          <FormControlLabel
            sx={{ mt: 1.5, alignItems: 'flex-start' }}
            control={<Checkbox checked={renonce} onChange={(e) => setRenonce(e.target.checked)} />}
            label={
              <Typography variant="body2">
                Je demande que l’abonnement commence tout de suite, et je renonce à mon droit de
                rétractation de 14 jours pour cette période.
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={enCours} onClick={() => setDialogue(false)}>
            Plus tard
          </Button>
          <Button variant="contained" disabled={!renonce || enCours} onClick={() => void payer()}>
            Payer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
